import { type FC, type MouseEvent } from 'react';
import { Menu, MenuItem } from '@material-ui/core';

import { ChevronIcon } from 'src/components/ChevronIcon';
// @ts-expect-error CustomChip doesn't have TypeScript definitions
import { CustomChip } from 'src/components/CustomChip';
import { CustomCircularProgress } from 'src/components/CustomCircularProgress';
import { ROTATE_DIRECTION } from 'src/data/ui';
import { usePopoverAnchor } from 'src/lib/hooks/usePopoverAnchor';
import { type Callback } from 'src/lib/types';

import { FreestyleToolLabel } from './components/FreestyleToolLabel';
import { type FreestyleTool } from './types';
import { useStyles } from './useStyles';

interface Props {
  className?: string;
  visibleTools: FreestyleTool[];
  hiddenTools: FreestyleTool[];
  loading?: boolean;
  onToolClick: Callback<FreestyleTool>;
}

export const FreestyleTools: FC<Props> = ({
  className,
  visibleTools,
  hiddenTools,
  loading,
  onToolClick,
}: Props) => {
  const styles = useStyles();

  const [anchorElement, openMenu, closeMenu] = usePopoverAnchor();
  const isMenuOpen = Boolean(anchorElement);

  const handleMoreClick = (event: MouseEvent<HTMLElement>) => {
    openMenu(event.currentTarget);
  };

  return (
    <div className={className}>
      {loading && (
        <div className={styles.loadingWrapper}>
          <CustomCircularProgress
            className={styles.loading}
            size={24}
          />
        </div>
      )}

      <div className={styles.chipsWrapper}>
        {visibleTools.map((freestyleTool) => (
          <CustomChip
            key={freestyleTool.id}
            className={styles.chip}
            variant="outlined"
            label={
              <FreestyleToolLabel
                className={styles.toolLabel}
                freestyleTool={freestyleTool}
              />
            }
            onClick={() => {
              onToolClick(freestyleTool);
            }}
          />
        ))}

        {hiddenTools.length > 0 && (
          <>
            <CustomChip
              className={styles.chip}
              variant="outlined"
              onClick={handleMoreClick}
              label={
                <div className={styles.chipLabel}>
                  <span>More</span>
                  <ChevronIcon
                    className={styles.iconExpand}
                    direction={isMenuOpen ? ROTATE_DIRECTION.up : ROTATE_DIRECTION.down}
                  />
                </div>
              }
            />
            <Menu
              classes={{ paper: styles.moreMenu }}
              anchorEl={anchorElement}
              open={isMenuOpen}
              onClose={closeMenu}
              getContentAnchorEl={null}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
            >
              {hiddenTools.map((freestyleTool) => (
                <MenuItem
                  className={styles.menuItem}
                  key={freestyleTool.id}
                  onClick={() => {
                    onToolClick(freestyleTool);
                    closeMenu();
                  }}
                >
                  <FreestyleToolLabel
                    className={styles.toolLabel}
                    freestyleTool={freestyleTool}
                  />
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
      </div>
    </div>
  );
};
